import { useEffect, useRef } from "react";
import {
  checkPlaceholders,
  extractEmailFromAddressText,
  formatDatetime,
} from "@_utils/helpers";
import { emailConstants } from "@_constants/email_flows.constants";

const DEFAULT_EMPTY_BODY = "<p>&nbsp;</ br></p>";

/**
 * Build the reply header HTML used in reply mode. Mirrors the original
 * inline template so the editor output is byte-identical to the legacy
 * implementation.
 */
function buildReplyHtml({ email, isSelfReply, emailSignature }) {
  return `<span><br></span><span><br></span>
    <div> ${emailSignature} </div> 
    <span><br></span>
    <hr style="display:inline-block;width:98%;color:black" tabindex="-1"></hr>
    <div id="divRplyFwdMsg" dir="ltr">
      <font face="Calibri, sans-serif" style="font-size:11pt" color="#000000">
      <b>From:</b> ${
        email.from && email.from.length > 0 ? email.from[0].address : ""
      }<br>
      <b>Sent:</b>  ${
        isSelfReply
          ? formatDatetime(email.created)
          : formatDatetime(email.data.date)
      }<br>
      <b>To:</b> ${
        email.to && email.to.length > 0 ? email.to[0].address : ""
      }<br>
      <b>Subject:</b> ${email.data.subject}</font>
      <div>&nbsp;</div></div>
      ${
        typeof email.data.html === "boolean"
          ? email.data.textAsHtml
          : "<div>" + email.data.html + "</div>"
      }
    `;
}

/**
 * Resolve the user signature for the editor body. Letters intentionally
 * skip the signature (the legacy behavior — letters are rendered server
 * side and must not have the email signature appended).
 */
function resolveEmailSignature(userSignature, dataType) {
  if (
    userSignature &&
    typeof userSignature === "string" &&
    userSignature.length > 0 &&
    dataType !== emailConstants.TEMPLATE_TYPE_LETTER
  ) {
    return userSignature;
  }
  return "";
}

function wrapBodyWith(content, trailingHtml) {
  const safeContent =
    content !== null && content !== "" && content?.length > 0
      ? content
      : DEFAULT_EMPTY_BODY;
  return "<div>" + safeContent + "</div>" + trailingHtml;
}

/**
 * Compute the recipient and cc addresses used when prefilling the reply.
 * Reply-all expands cc from email.data.cc.value, excluding the current
 * user's own SMTP address.
 */
export function computeReplyAddresses({
  email,
  isSelfReply,
  replyToAll,
  JwtData,
}) {
  const source = isSelfReply === true ? "to" : "from";
  let toList = "";
  let ccMail = "";

  if (replyToAll === true && source === "from") {
    toList = extractEmailFromAddressText(
      email.from && email.from.length
        ? email.from[0].address !== JwtData?.smtp_email
          ? email.from[0].address
          : ""
        : "",
    );
    if (email.to && email.to.length) {
      email.to.forEach((entry) => {
        if (entry.address !== JwtData?.smtp_email) {
          toList =
            toList.length > 0
              ? toList + ";" + extractEmailFromAddressText(entry.address)
              : extractEmailFromAddressText(entry.address);
        }
      });
    }
    if (email.data?.cc?.value?.length) {
      email.data.cc.value.forEach((entry) => {
        if (entry.address !== JwtData?.smtp_email) {
          ccMail =
            ccMail.length > 0
              ? ccMail + ";" + extractEmailFromAddressText(entry.address)
              : extractEmailFromAddressText(entry.address);
        }
      });
    }
  }

  const to = extractEmailFromAddressText(
    email[source] && email[source].length
      ? Array.isArray(email[source])
        ? email[source][0].address
        : email[source]
      : "",
  );

  return { to, cc: replyToAll && ccMail ? ccMail : "" };
}

/**
 * Single source of truth for initializing the MailPopup editor and
 * input fields. Replaces three overlapping useEffects (reply init,
 * non-reply with signature, non-reply duplicate) that previously
 * fought over `data.content` and could leave the editor in an
 * inconsistent state when re-opened or when `data.content` changed.
 *
 * Behaviors preserved from the legacy effects:
 *  - errorSnackbar is reset on every (re)open / data change
 *  - sendEmailTag follows the presence of emailSetup
 *  - reply mode injects the email-history HTML and prefills to/subject/cc
 *  - reply-all populates cc from email.data.cc.value
 *  - non-reply with a letter template does NOT append the user signature
 *  - non-reply with an email template DOES append the user signature
 *  - while a send is in-flight (loading === true) the reply branch skips
 *    re-initialization so it cannot clobber the user's draft
 *  - placeholders are detected and `missingPlaceholder` flag updates
 */
export function useEditorInitialization({
  open,
  editorReady,
  isClosingRef,
  isReplyMode,
  loading,
  data,
  email,
  isSelfReply,
  replyToAll,
  JwtData,
  emailSetup,
  userSignature,
  setTextToContent,
  setInputs,
  setSendEmailTag,
  setErrorSnackbar,
  setDidPreviewLetter,
  setMissingPlaceholder,
}) {
  // Setters/callbacks are funneled through a ref so the effect can stay
  // pure on data-driven deps without re-firing every parent render and
  // without an exhaustive-deps eslint-disable.
  const callbacksRef = useRef(null);
  callbacksRef.current = {
    setTextToContent,
    setInputs,
    setSendEmailTag,
    setErrorSnackbar,
    setDidPreviewLetter,
    setMissingPlaceholder,
  };

  // `loading` is intentionally read via ref so a send-failure flip from
  // true to false does not re-fire the I  and wipe the user's draft.
  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  // Same reasoning for isSelfReply / JwtData — they're only consulted
  // inside the reply branch and shouldn't trigger re-init on identity
  // changes that don't affect the rendered output.
  const isSelfReplyRef = useRef(isSelfReply);
  isSelfReplyRef.current = isSelfReply;
  const JwtDataRef = useRef(JwtData);
  JwtDataRef.current = JwtData;

  useEffect(() => {
    if (!open || !editorReady) return;
    if (isClosingRef?.current) return;

    const cb = callbacksRef.current;

    if (isReplyMode && email) {
      if (loadingRef.current === true) return;

      cb.setErrorSnackbar(false);
      cb.setSendEmailTag(Boolean(emailSetup));

      const emailSignature = resolveEmailSignature(userSignature, data?.type);
      const replyHtml = buildReplyHtml({
        email,
        isSelfReply: isSelfReplyRef.current,
        emailSignature,
      });

      if (data?.content) {
        cb.setTextToContent(wrapBodyWith(data.content, replyHtml), true);
      } else {
        cb.setMissingPlaceholder(checkPlaceholders(replyHtml));
        cb.setTextToContent(replyHtml);
        cb.setDidPreviewLetter(false);
      }

      const { to, cc } = computeReplyAddresses({
        email,
        isSelfReply: isSelfReplyRef.current,
        replyToAll,
        JwtData: JwtDataRef.current,
      });

      cb.setInputs({
        to,
        subject: "Re: " + email.data.subject,
        cc,
        content: replyHtml,
      });
      return;
    }

    if (!isReplyMode) {
      cb.setErrorSnackbar(false);
      cb.setDidPreviewLetter(false);
      cb.setSendEmailTag(Boolean(emailSetup));

      const emailSignature = resolveEmailSignature(userSignature, data?.type);

      if (emailSignature) {
        cb.setTextToContent(
          wrapBodyWith(data?.content, "<div>" + emailSignature + "</div>"),
          true,
        );
      } else {
        cb.setTextToContent(data?.content ?? DEFAULT_EMPTY_BODY, true);
      }

      cb.setInputs({
        to: data?.opponentEmail,
        subject: data?.subject,
        content: data?.content,
      });
    }
  }, [
    open,
    editorReady,
    isClosingRef,
    isReplyMode,
    data?.content,
    data?.opponentEmail,
    data?.subject,
    data?.type,
    emailSetup,
    email,
    replyToAll,
    userSignature,
  ]);
}

export const __internals = {
  buildReplyHtml,
  resolveEmailSignature,
  wrapBodyWith,
};
